import { Eye, EyeOff, InfoIcon, Lock, PencilRuler } from 'lucide-react';
import { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const postStatusBadgeVariants = cva('p-2', {
  variants: {
    visibility: {
      private: 'bg-orange-500',
      public: 'bg-green-500',
      unlisted: 'bg-black',
      draft: 'bg-yellow-500',
    },
  },
  defaultVariants: {
    visibility: 'public',
  },
});

interface PostStatusBadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof postStatusBadgeVariants> {
  visibility: 'private' | 'public' | 'unlisted' | 'draft';
}

function PostStatusIcon({
  visibility,
  size,
}: {
  visibility: 'private' | 'public' | 'unlisted' | 'draft';
  size: string;
}) {
  switch (visibility) {
    case 'public':
      return <Eye size={size} />;
    case 'private':
      return <Lock size={size} />;
    case 'draft':
      return <PencilRuler size={size} />;
    case 'unlisted':
      return <EyeOff size={size} />;
    default:
      return <InfoIcon size={size} />;
  }
}

function PostStatusBadge({
  className,
  visibility,
  ...props
}: PostStatusBadgeProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Badge
            className={cn(postStatusBadgeVariants({ visibility }), className)}
            {...props}
          >
            <PostStatusIcon visibility={visibility} size="16" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <span className="capitalize text-md">{visibility}&nbsp;Project</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default PostStatusBadge;
